import React from "react";
import { Table, Checkbox, Button, Card } from "antd";
import { StorageCalculation, Booking } from "../../types";

interface StorageTableProps {
  selectedServices: string[];
  toggleStorageSelection: () => void;
  includeInsurance: boolean;
  setIncludeInsurance: (value: boolean) => void;
  storageDates: StorageCalculation[];
  averageStorage: number;
  storageRate: number;
  totalInventoryValue: number;
  insuranceRate: number;
  booking: Booking | undefined;
}

export const StorageTable: React.FC<StorageTableProps> = ({
  selectedServices,
  toggleStorageSelection,
  includeInsurance,
  setIncludeInsurance,
  storageDates,
  averageStorage,
  storageRate,
  totalInventoryValue,
  insuranceRate,
  booking,
}) => {
  const storageColumns = [
    {
      title: "Select",
      key: "select",
      render: () => (
        <Checkbox
          checked={selectedServices.includes("storage")}
          onChange={toggleStorageSelection}
        />
      ),
      width: 80,
    },
    {
      title: "Type",
      key: "type",
      render: () => "Monthly Storage",
    },
    {
      title: "Month",
      key: "month",
      render: () => {
        const date = new Date();
        return `${date.toLocaleString("default", {
          month: "long",
        })} ${date.getFullYear()}`;
      },
    },
    {
      title: "Avg. Cubic Ft",
      key: "avgCubicFt",
      render: () => averageStorage.toFixed(2),
    },
    {
      title: "Rate",
      key: "rate",
      render: () => `$${storageRate.toFixed(2)}/cubic ft`,
    },
    {
      title: "Insurance",
      key: "insurance",
      render: () => (
        <Checkbox
          checked={includeInsurance}
          onChange={(e) => setIncludeInsurance(e.target.checked)}
          disabled={!selectedServices.includes("storage")}
        >
          Include Insurance ({(insuranceRate * 100).toFixed(1)}%)
        </Checkbox>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      render: () => {
        const storageAmount = averageStorage * storageRate;
        const insuranceAmount = includeInsurance
          ? totalInventoryValue * insuranceRate
          : 0;
        const total = storageAmount + insuranceAmount;
        return `$${total.toFixed(2)}`;
      },
    },
    {
      title: "Details",
      key: "expand",
      render: () => <Button type="link">View Details</Button>,
    },
  ];

  const expandedStorageRowRender = () => (
    <Card className="bg-gray-50 p-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium mb-2">Storage Calculation</h4>
          <p>
            Storage is calculated based on the average cubic footage over the
            last 5 days of the month.
          </p>
          <table className="w-full text-sm mt-3">
            <thead>
              <tr className="border-b">
                <th className="text-left">Date</th>
                <th className="text-right">Cubic Footage</th>
              </tr>
            </thead>
            <tbody>
              {storageDates.map((sample) => (
                <tr key={sample.date} className="border-b">
                  <td>{sample.date}</td>
                  <td className="text-right">
                    {sample.cubicFootage.toFixed(2)}
                  </td>
                </tr>
              ))}
              <tr className="font-medium">
                <td>Average:</td>
                <td className="text-right">{averageStorage.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <h4 className="font-medium mb-2">Rate Information</h4>
          <p>
            <strong>Location:</strong> {booking?.location?.name ?? "N/A"}
          </p>
          <p>
            <strong>Base Rate:</strong> ${storageRate.toFixed(2)}/cubic ft
          </p>
          {includeInsurance && (
            <>
              <h5 className="font-medium mt-4 mb-2">Insurance Details</h5>
              <p>
                <strong>Total Inventory Value:</strong> $
                {totalInventoryValue.toFixed(2)}
              </p>
              <p>
                <strong>Insurance Rate:</strong>{" "}
                {(insuranceRate * 100).toFixed(1)}%
              </p>
              <p>
                <strong>Monthly Insurance:</strong> $
                {(totalInventoryValue * insuranceRate).toFixed(2)}
              </p>
            </>
          )}
        </div>
      </div>
    </Card>
  );

  return (
    <Table
      dataSource={[{ id: "storage" }]}
      columns={storageColumns}
      rowKey="id"
      expandable={{
        expandedRowRender: expandedStorageRowRender,
        expandRowByClick: true,
      }}
      pagination={false}
    />
  );
};
